import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  Play, ArrowLeft, Download, RefreshCw, 
  Volume2, Video, CheckCircle2, Clock,
  AlertCircle, Sparkles, Instagram, Linkedin,
  Mic, Film, Lock, Crown, Type
} from "lucide-react";

import { API_URL, BASE_URL } from "../config/api";

const API = API_URL;
const BACKEND = BASE_URL;

const ProjectDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isGeneratingFromCreate = searchParams.get('generating') === 'true';
  const { getAuthHeaders, canDownload, getMaxDuration, user } = useAuth();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [voiceText, setVoiceText] = useState("");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoDuration, setVideoDuration] = useState(getMaxDuration());
  const [generatingVoice, setGeneratingVoice] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [addingSubtitles, setAddingSubtitles] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [isStartingGeneration, setIsStartingGeneration] = useState(false);

  // Helper function - defined first as it's used by fetchProject
  const generateVideoPrompt = useCallback((proj) => {
    if (!proj) return "";
    const platformDesc = {
      instagram: "Instagram Reel advertentie",
      tiktok: "TikTok advertentie video",
      linkedin: "LinkedIn professionele advertentie"
    };
    return `Maak een professionele ${platformDesc[proj.platform] || 'social media advertentie'} video. ${proj.ad_text}. De video moet aantrekkelijk en dynamisch zijn met moderne overgangen en effecten.`;
  }, []);

  // Define fetchProject first (before useEffect that uses it)
  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`, {
        headers: getAuthHeaders()
      });
      setProject(response.data);
      setVoiceText(response.data.ad_text || "");
      setVideoPrompt(generateVideoPrompt(response.data));
    } catch (error) {
      toast.error("Kon project niet laden");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate, generateVideoPrompt]);

  // Define fetchVoices (before useEffect that uses it)
  const fetchVoices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/voices`, {
        headers: getAuthHeaders()
      });
      setVoices(response.data);
      if (response.data.length > 0) {
        const dutchVoice = response.data.find(v => 
          v.name.toLowerCase().includes('dutch') || 
          v.name.toLowerCase().includes('nl')
        );
        setSelectedVoice(dutchVoice?.voice_id || response.data[0].voice_id);
      }
    } catch (error) {
      if (error.response?.status >= 500) {
        toast.error("Kon stemmen niet laden.");
      }
    }
  }, [getAuthHeaders]);

  const checkStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/status`, {
        headers: getAuthHeaders()
      });
      
      // Always update progress and message
      const newProgress = response.data.progress || 0;
      const newMessage = response.data.progress_message || "";
      
      setProgress(newProgress);
      setProgressMessage(newMessage);
      
      // Always update the project with fresh data from server
      setProject(prev => {
        if (!prev) return prev;
        return { 
          ...prev, 
          status: response.data.status,
          progress: newProgress,
          progress_message: newMessage,
          video_url: response.data.video_url,
          voice_over_url: response.data.voice_over_url,
          error_message: response.data.error_message
        };
      });
      
      // Show toast on status change
      if (response.data.status === "completed" && project?.status !== "completed") {
        toast.success("Video is klaar!");
      } else if (response.data.status === "failed" && project?.status !== "failed") {
        toast.error(response.data.error_message || "Video generatie mislukt");
      }
    } catch (error) {
      // Status check silently retries on next interval
      if (error.code === 'ERR_NETWORK') {
        // Network issue - will auto-retry
      }
    }
  }, [id, getAuthHeaders, project?.status]);

  // Fetch project and voices on mount
  useEffect(() => {
    fetchProject();
    fetchVoices();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [fetchProject, fetchVoices]);

  // Polling effect - uses ref to avoid dependency issues
  useEffect(() => {
    const shouldPoll = project?.status === "generating" || isStartingGeneration || isGeneratingFromCreate;
    
    if (shouldPoll && !pollIntervalRef.current) {
      // Start polling immediately and then every 2 seconds for faster updates
      checkStatus();
      pollIntervalRef.current = setInterval(checkStatus, 2000);
    }
    
    if (!shouldPoll && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, [project?.status, checkStatus, isStartingGeneration, isGeneratingFromCreate]);

  const handleGenerateVoice = async () => {
    if (!voiceText.trim()) {
      toast.error("Voer eerst een tekst in");
      return;
    }
    
    setGeneratingVoice(true);
    try {
      await axios.post(`${API}/projects/${id}/generate-voiceover`, {
        project_id: id,
        text: voiceText,
        voice_id: selectedVoice,
        duration: videoDuration  // Send selected duration for audio sync
      }, {
        headers: getAuthHeaders()
      });
      
      await fetchProject();
      toast.success("Voice-over gegenereerd!");
    } catch (error) {
      const errorDetail = error.response?.data?.detail || "Kon voice-over niet genereren";
      if (errorDetail.includes("beperkte permissies") || errorDetail.includes("ElevenLabs")) {
        toast.error("ElevenLabs API key heeft beperkte permissies. Neem contact op met de beheerder.", {
          duration: 6000  // Extended duration for important error messages
        });
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setGeneratingVoice(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!videoPrompt.trim()) {
      toast.error("Voer eerst een video prompt in");
      return;
    }
    
    setGeneratingVideo(true);
    try {
      await axios.post(`${API}/projects/${id}/generate-video`, {
        project_id: id,
        video_prompt: videoPrompt,
        duration: videoDuration
      }, {
        headers: getAuthHeaders()
      });
      
      setProject(prev => ({ ...prev, status: "generating" }));
      toast.info(`Video generatie gestart (${videoDuration} sec). Dit kan enkele minuten duren...`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kon video niet genereren");
    } finally {
      setGeneratingVideo(false);
    }
  };

  const handleAddSubtitles = async () => {
    if (!voiceText.trim()) {
      toast.error("Voer eerst een tekst in voor de ondertiteling");
      return;
    }
    
    if (!project.video_url) {
      toast.error("Genereer eerst een video voordat je ondertitels toevoegt");
      return;
    }
    
    setAddingSubtitles(true);
    try {
      const response = await axios.post(`${API}/projects/${id}/add-subtitles`, {
        project_id: id,
        text: voiceText
      }, {
        headers: getAuthHeaders()
      });
      
      // Refresh project to get new video URL
      await fetchProject();
      toast.success("Ondertitels toegevoegd!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kon ondertitels niet toevoegen");
    } finally {
      setAddingSubtitles(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "instagram":
        return <Instagram className="w-5 h-5" />;
      case "tiktok":
        return <Video className="w-5 h-5" />;
      case "linkedin":
        return <Linkedin className="w-5 h-5" />;
      default:
        return <Video className="w-5 h-5" />;
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "completed":
        return { icon: <CheckCircle2 className="w-5 h-5" />, text: "Voltooid", color: "text-emerald-400" };
      case "generating":
        return { icon: <Clock className="w-5 h-5 animate-spin" />, text: "Bezig met genereren...", color: "text-amber-400" };
      case "failed":
        return { icon: <AlertCircle className="w-5 h-5" />, text: "Mislukt", color: "text-red-400" };
      default:
        return { icon: <Clock className="w-5 h-5" />, text: "Concept", color: "text-slate-400" };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!project) return null;

  const statusInfo = getStatusInfo(project.status);

  // Show simplified generating view when video is being generated OR when coming from create wizard
  if (project.status === "generating" || project.status === "adding_subtitles" || (isGeneratingFromCreate && project.status === "draft")) {
    return (
      <div className="min-h-screen bg-slate-950 dark-mode flex flex-col">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Terug</span>
            </Link>
            <div className="flex items-center gap-2 text-amber-400">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span className="font-medium">Bezig met genereren...</span>
            </div>
          </div>
        </nav>

        {/* Centered Generating View */}
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-lg w-full text-center">
            {/* Animated Icon */}
            <div className="mb-8">
              <div className="w-32 h-32 mx-auto rounded-full bg-indigo-600/20 flex items-center justify-center relative">
                <Sparkles className="w-16 h-16 text-indigo-400 animate-pulse" />
                <div className="absolute inset-0 rounded-full border-4 border-indigo-500/30 border-t-indigo-500 animate-spin"></div>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {project.status === "adding_subtitles" ? "Ondertitels Toevoegen..." : "Video Wordt Gegenereerd..."}
            </h1>

            {/* Project Name */}
            <p className="text-slate-400 text-lg mb-8">
              {project.name}
            </p>

            {/* Progress */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 mb-6">
              <div className="flex justify-between items-center mb-3">
                <span className="text-slate-400">Voortgang</span>
                <span className="text-indigo-400 font-bold text-xl">{progress}%</span>
              </div>
              <Progress value={progress} className="h-3 bg-slate-800" />
              {progressMessage && (
                <p className="text-slate-500 text-sm mt-3">{progressMessage}</p>
              )}
            </div>

            {/* Info */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
              <p className="text-slate-400 text-sm">
                <Clock className="w-4 h-4 inline mr-2" />
                Dit kan 2-5 minuten duren. Je kunt deze pagina verlaten en later terugkomen.
              </p>
            </div>

            {/* Platform Badge */}
            <div className="mt-6">
              <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
                {getPlatformIcon(project.platform)}
                <span className="ml-1 capitalize">{project.platform}</span>
                <span className="ml-2">• {project.duration || 12} sec</span>
              </Badge>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show failed view with error message
  if (project.status === "failed") {
    return (
      <div className="min-h-screen bg-slate-950 dark-mode flex flex-col">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Terug</span>
            </Link>
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Mislukt</span>
            </div>
          </div>
        </nav>

        {/* Centered Failed View */}
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-lg w-full text-center">
            {/* Error Icon */}
            <div className="mb-8">
              <div className="w-32 h-32 mx-auto rounded-full bg-red-600/20 flex items-center justify-center">
                <AlertCircle className="w-16 h-16 text-red-400" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Video Generatie Mislukt
            </h1>

            {/* Project Name */}
            <p className="text-slate-400 text-lg mb-6">
              {project.name}
            </p>

            {/* Error Message */}
            <div className="bg-red-900/20 border border-red-800 rounded-2xl p-6 mb-6">
              <p className="text-red-300">
                {project.error_message || "Er is een onbekende fout opgetreden."}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={() => {
                  setProject(prev => ({ ...prev, status: "draft" }));
                  setIsStartingGeneration(false);
                }}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-5"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Opnieuw Proberen
              </Button>
              
              <Button 
                variant="outline"
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 py-5"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Terug naar Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // For draft/concept status - show simple "Start Generation" view instead of complex panels
  if (project.status === "draft" && !project.video_url && !isStartingGeneration) {
    
    const handleStartGeneration = async () => {
      // Immediately show generating view by updating local state
      setIsStartingGeneration(true);
      setProgress(0);
      setProgressMessage("Video generatie starten...");
      
      // Update project status locally to show generating view
      setProject(prev => ({ ...prev, status: "generating" }));
      
      try {
        // Generate voice-over first (in background)
        if (project.ad_text) {
          setProgressMessage("Voice-over genereren...");
          await axios.post(`${API}/projects/${project.id}/generate-voiceover`, {
            project_id: project.id,
            text: project.ad_text,
            voice_id: "21m00Tcm4TlvDq8ikWAM",
            duration: project.duration || 8
          }, { headers: getAuthHeaders() });
        }
        
        // Start video generation
        setProgressMessage("Video generatie starten...");
        const platformDesc = {
          instagram: "Instagram Reel advertentie",
          tiktok: "TikTok advertentie video",
          linkedin: "LinkedIn professionele advertentie"
        };
        const videoPromptText = `Maak een professionele ${platformDesc[project.platform] || 'social media advertentie'} video. ${project.ad_text || project.description || ''}. De video moet aantrekkelijk en dynamisch zijn.`;
        
        await axios.post(`${API}/projects/${project.id}/generate-video`, {
          project_id: project.id,
          video_prompt: videoPromptText,
          duration: project.duration || 8
        }, { headers: getAuthHeaders() });
        
        toast.success("Video generatie gestart!");
        // Polling will update progress from here
      } catch (error) {
        toast.error(error.response?.data?.detail || "Kon generatie niet starten");
        // Revert to draft status on error
        setProject(prev => ({ ...prev, status: "draft" }));
        setIsStartingGeneration(false);
      }
    };

    return (
      <div className="min-h-screen bg-slate-950 dark-mode flex flex-col">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Terug</span>
            </Link>
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Concept</span>
            </div>
          </div>
        </nav>

        {/* Centered Start Generation View */}
        <main className="flex-1 flex items-center justify-center px-6 pt-20">
          <div className="max-w-xl w-full text-center">
            {/* Product Image Preview */}
            {project.product_image_url && (
              <div className="mb-8">
                <img 
                  src={`${BACKEND}${project.product_image_url}`}
                  alt={project.name}
                  className="w-48 h-48 mx-auto object-cover rounded-2xl border-2 border-slate-700"
                />
              </div>
            )}

            {/* Title */}
            <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 mb-4">
              {getPlatformIcon(project.platform)}
              <span className="ml-1 capitalize">{project.platform}</span>
              <span className="ml-2">• {project.duration || 8} sec</span>
            </Badge>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {project.name}
            </h1>

            {/* Ad Text Preview */}
            {project.ad_text && (
              <p className="text-slate-400 text-lg mb-8 max-w-md mx-auto">
                "{project.ad_text}"
              </p>
            )}

            {/* Start Button */}
            <Button 
              onClick={handleStartGeneration}
              disabled={isStartingGeneration}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-12 py-6 text-lg rounded-xl"
              data-testid="start-generation-btn"
            >
              {isStartingGeneration ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Bezig met starten...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Video Genereren
                </>
              )}
            </Button>

            <p className="text-slate-500 text-sm mt-4">
              Klik om je AI-gegenereerde video te maken
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Simplified completed view - just show the video with download
  if (project.status === "completed" && project.video_url) {
    return (
      <div className="min-h-screen bg-slate-950 dark-mode flex flex-col">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Terug</span>
            </Link>
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Voltooid</span>
            </div>
          </div>
        </nav>

        {/* Centered Completed View */}
        <main className="flex-1 flex items-center justify-center px-6 pt-20">
          <div className="max-w-2xl w-full">
            {/* Header */}
            <div className="text-center mb-6">
              <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 mb-3">
                {getPlatformIcon(project.platform)}
                <span className="ml-1 capitalize">{project.platform}</span>
                <span className="ml-2">• {project.duration || 12} sec</span>
              </Badge>
              <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {project.name}
              </h1>
            </div>

            {/* Video Player with Synced Voice-over */}
            <Card className="bg-slate-900/80 border-slate-800 overflow-hidden mb-6">
              <CardContent className="p-4">
                <div className="relative">
                  <video 
                    id="main-video"
                    controls 
                    className="w-full rounded-lg"
                    src={`${BACKEND}${project.video_url}`}
                    poster={project.product_image_url ? `${BACKEND}${project.product_image_url}` : undefined}
                    onPlay={() => {
                      // Sync voice-over with video playback
                      const audio = document.getElementById('voice-over-audio');
                      if (audio && project.voice_over_url) {
                        audio.currentTime = document.getElementById('main-video').currentTime;
                        audio.play().catch(() => {});
                      }
                    }}
                    onPause={() => {
                      const audio = document.getElementById('voice-over-audio');
                      if (audio) audio.pause();
                    }}
                    onSeeked={() => {
                      const audio = document.getElementById('voice-over-audio');
                      const video = document.getElementById('main-video');
                      if (audio && video) {
                        audio.currentTime = video.currentTime;
                      }
                    }}
                    onEnded={() => {
                      const audio = document.getElementById('voice-over-audio');
                      if (audio) {
                        audio.pause();
                        audio.currentTime = 0;
                      }
                    }}
                    onError={(e) => {
                      console.error("Video playback error:", e);
                      const errorDiv = document.getElementById('video-error-msg');
                      if (errorDiv) errorDiv.classList.remove('hidden');
                    }}
                  >
                    Je browser ondersteunt geen video.
                  </video>
                  
                  {/* Hidden audio element for voice-over sync */}
                  {project.voice_over_url && (
                    <audio 
                      id="voice-over-audio"
                      src={`${BACKEND}${project.voice_over_url}`}
                      preload="auto"
                      style={{ display: 'none' }}
                    />
                  )}
                  
                  {/* Voice-over indicator */}
                  {project.voice_over_url && (
                    <div className="absolute bottom-14 right-3 bg-black/70 px-2 py-1 rounded text-xs text-emerald-400 flex items-center gap-1">
                      <Volume2 className="w-3 h-3" />
                      Voice-over actief
                    </div>
                  )}
                </div>
                
                {/* Video Error Message with Fix Button (Admin only) */}
                <div id="video-error-msg" className="hidden mt-4 p-4 bg-red-900/30 border border-red-800 rounded-lg">
                  <p className="text-red-400 text-sm mb-3">
                    Video kan niet worden afgespeeld. Dit komt meestal door een incompatibel videoformaat.
                  </p>
                  {user?.is_admin && (
                    <Button
                      size="sm"
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={async () => {
                        try {
                          toast.info("Video wordt geconverteerd...");
                          await axios.post(`${API}/admin/fix-video/${project.id}`, {}, {
                            headers: getAuthHeaders()
                          });
                          toast.success("Video geconverteerd! Pagina wordt herladen...");
                          setTimeout(() => window.location.reload(), 1500);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Kon video niet converteren");
                        }
                      }}
                      data-testid="fix-video-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Video Repareren
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              {canDownload() ? (
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = `${BACKEND}${project.video_url}`;
                    link.download = `${project.name}.mp4`;
                    link.click();
                  }}
                  data-testid="download-video-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Video Downloaden
                </Button>
              ) : (
                <Button 
                  className="flex-1 bg-slate-700 text-slate-400 py-6 text-lg cursor-not-allowed"
                  disabled
                >
                  <Lock className="w-5 h-5 mr-2" />
                  Upgrade voor Download
                </Button>
              )}
              
              <Button 
                variant="outline"
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 py-6 text-lg"
                onClick={() => navigate('/create')}
                data-testid="new-video-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Nieuwe Video
              </Button>
            </div>

            {/* Edit options - collapsed (includes voice-over) */}
            <details className="mt-4">
              <summary className="text-slate-500 text-sm cursor-pointer hover:text-slate-400 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Meer opties (voice-over, ondertiteling, opnieuw genereren)
              </summary>
              <div className="mt-4 p-4 bg-slate-900/50 border border-slate-800 rounded-xl space-y-4">
                {/* Voice-over regenerate */}
                <div>
                  <Label className="text-slate-400 text-sm">Voice-over opnieuw genereren</Label>
                  <div className="flex gap-2 mt-2">
                    <Textarea
                      value={voiceText}
                      onChange={(e) => setVoiceText(e.target.value)}
                      placeholder="Tekst voor voice-over"
                      className="bg-slate-800 border-slate-700 text-white flex-1"
                      rows={2}
                    />
                    <Button
                      onClick={handleGenerateVoice}
                      disabled={generatingVoice || !voiceText.trim()}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      {generatingVoice ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                {/* Subtitles */}
                <div>
                  <Button
                    onClick={handleAddSubtitles}
                    disabled={addingSubtitles || !voiceText.trim()}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                  >
                    {addingSubtitles ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Ondertitels toevoegen...</>
                    ) : (
                      <><Type className="w-4 h-4 mr-2" /> {project.has_subtitles ? "Ondertitels Opnieuw" : "Ondertitels Toevoegen"}</>
                    )}
                  </Button>
                </div>
              </div>
            </details>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 dark-mode">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Terug</span>
          </Link>
          <div className={`flex items-center gap-2 ${statusInfo.color}`}>
            {statusInfo.icon}
            <span className="font-medium">{statusInfo.text}</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
                  {getPlatformIcon(project.platform)}
                  <span className="ml-1 capitalize">{project.platform}</span>
                </Badge>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {project.name}
              </h1>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Preview */}
            <div className="space-y-6">
              {/* Product Image */}
              <Card className="bg-slate-900/80 border-slate-800 overflow-hidden">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <Film className="w-5 h-5 text-indigo-400" />
                    Product Afbeelding
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="aspect-video bg-slate-800 flex items-center justify-center">
                    {project.product_image_url ? (
                      <img 
                        src={`${BACKEND}${project.product_image_url}`}
                        alt={project.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="text-slate-500 flex flex-col items-center gap-2">
                        <Film className="w-12 h-12" />
                        <span>Geen afbeelding</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Generated Video */}
              {project.video_url && (
                <Card className="bg-slate-900/80 border-slate-800 overflow-hidden">
                  <CardHeader className="border-b border-slate-800">
                    <CardTitle className="text-white flex items-center gap-2 text-lg">
                      <Video className="w-5 h-5 text-emerald-400" />
                      Gegenereerde Video
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <video 
                      controls 
                      playsInline
                      preload="metadata"
                      className="w-full rounded-lg"
                      style={{ maxHeight: '400px' }}
                    >
                      <source src={`${BACKEND}${project.video_url}`} type="video/mp4" />
                      <source src={`${BACKEND}${project.video_url}`} type="video/webm" />
                      Je browser ondersteunt geen video. 
                      <a href={`${BACKEND}${project.video_url}`} download>Download de video</a>
                    </video>
                    
                    {canDownload() ? (
                      <Button 
                        data-testid="download-video-btn"
                        className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={async () => {
                          try {
                            // Check and track download
                            const checkRes = await axios.get(`${API}/projects/${id}/can-download`, {
                              headers: getAuthHeaders()
                            });
                            
                            if (!checkRes.data.can_download) {
                              toast.error(checkRes.data.message || "Je kunt geen video's downloaden");
                              return;
                            }
                            
                            // Track the download
                            const trackRes = await axios.post(`${API}/projects/${id}/track-download`, {}, {
                              headers: getAuthHeaders()
                            });
                            
                            toast.success(trackRes.data.message);
                            
                            // Start download
                            const link = document.createElement('a');
                            link.href = `${BACKEND}${project.video_url}`;
                            link.download = `${project.name}.mp4`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          } catch (error) {
                            const msg = error.response?.data?.detail || "Download mislukt";
                            toast.error(msg);
                          }
                        }}
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Video Downloaden
                      </Button>
                    ) : (
                      <div className="mt-4">
                        <Link to="/subscription">
                          <Button 
                            data-testid="upgrade-to-download-btn"
                            className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
                          >
                            <Lock className="w-5 h-5 mr-2" />
                            Upgrade om te Downloaden
                          </Button>
                        </Link>
                        <p className="text-xs text-slate-500 text-center mt-2">
                          Met een abonnement kun je video's downloaden
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Voice Over Player */}
              {project.voice_over_url && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader className="border-b border-slate-800">
                    <CardTitle className="text-white flex items-center gap-2 text-lg">
                      <Volume2 className="w-5 h-5 text-indigo-400" />
                      Voice-over
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <audio 
                      ref={audioRef}
                      controls 
                      preload="metadata"
                      className="w-full"
                      style={{ minHeight: '40px' }}
                    >
                      <source src={`${BACKEND}${project.voice_over_url}`} type="audio/mpeg" />
                      <source src={`${BACKEND}${project.voice_over_url}`} type="audio/mp3" />
                      Je browser ondersteunt geen audio.
                    </audio>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Actions */}
            <div className="space-y-6">
              {/* Voice Over Generator */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <Mic className="w-5 h-5 text-indigo-400" />
                    Voice-over Genereren
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Stem Kiezen</Label>
                    <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                      <SelectTrigger 
                        data-testid="voice-select"
                        className="bg-slate-800 border-slate-700 text-white"
                      >
                        <SelectValue placeholder="Selecteer een stem" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {voices.map((voice) => (
                          <SelectItem 
                            key={voice.voice_id} 
                            value={voice.voice_id}
                            className="text-white hover:bg-slate-700"
                          >
                            {voice.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Tekst voor Voice-over</Label>
                    <Textarea
                      data-testid="voice-text-input"
                      value={voiceText}
                      onChange={(e) => setVoiceText(e.target.value)}
                      rows={4}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                      placeholder="Voer de tekst in die je wilt laten uitspreken..."
                    />
                  </div>

                  <Button
                    data-testid="generate-voice-btn"
                    onClick={handleGenerateVoice}
                    disabled={generatingVoice || !selectedVoice}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {generatingVoice ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Genereren...
                      </div>
                    ) : (
                      <>
                        <Volume2 className="w-5 h-5 mr-2" />
                        {project.voice_over_url ? "Voice-over Opnieuw Genereren" : "Voice-over Genereren"}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Video Generator */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    Video Genereren (Sora 2)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Video Beschrijving (Prompt)</Label>
                    <Textarea
                      data-testid="video-prompt-input"
                      value={videoPrompt}
                      onChange={(e) => setVideoPrompt(e.target.value)}
                      rows={4}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                      placeholder="Beschrijf de video die je wilt maken..."
                    />
                    <p className="text-xs text-slate-500">
                      Tip: Beschrijf de stijl, sfeer en acties die je in de video wilt zien
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Video Duur</Label>
                    <Select value={String(videoDuration)} onValueChange={(val) => setVideoDuration(Number(val))}>
                      <SelectTrigger 
                        data-testid="duration-select"
                        className="bg-slate-800 border-slate-700 text-white"
                      >
                        <SelectValue placeholder="Kies video duur" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="4" className="text-white hover:bg-slate-700">4 seconden</SelectItem>
                        {(user?.subscription_plan !== "demo" || user?.is_admin) && (
                          <>
                            <SelectItem value="8" className="text-white hover:bg-slate-700">8 seconden</SelectItem>
                            <SelectItem value="12" className="text-white hover:bg-slate-700">12 seconden (max)</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {user?.subscription_plan === "demo" && !user?.is_admin 
                        ? "Demo account: maximaal 4 seconden. Upgrade voor langere video's!"
                        : "Sora 2 ondersteunt momenteel maximaal 12 seconden per video"
                      }
                    </p>
                  </div>

                  {project.status === "generating" && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-400 animate-spin" />
                          </div>
                          <div>
                            <span className="text-amber-400 font-medium block">Video wordt gegenereerd</span>
                            <span className="text-slate-400 text-sm">{progressMessage || "Bezig..."}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-amber-400">{progress}%</span>
                        </div>
                      </div>
                      <Progress className="h-3 bg-slate-800" value={progress} />
                      <p className="text-slate-500 text-xs mt-2">
                        Gemiddelde generatietijd: 3-5 minuten. Je kunt deze pagina open laten.
                      </p>
                    </div>
                  )}

                  {project.status === "failed" && project.error_message && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                        <span className="text-red-400 font-medium">Video generatie mislukt</span>
                      </div>
                      <p className="text-slate-400 text-sm">
                        {project.error_message}
                      </p>
                    </div>
                  )}

                  <Button
                    data-testid="generate-video-btn"
                    onClick={handleGenerateVideo}
                    disabled={generatingVideo || project.status === "generating"}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  >
                    {generatingVideo || project.status === "generating" ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Bezig...
                      </div>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        {project.video_url ? "Video Opnieuw Genereren" : "Video Genereren"}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Subtitles Section */}
              {project.video_url && (
                <Card className="bg-slate-900/80 border-slate-800">
                  <CardHeader className="border-b border-slate-800">
                    <CardTitle className="text-white flex items-center gap-2 text-lg">
                      <Type className="w-5 h-5 text-cyan-400" />
                      Ondertiteling Toevoegen
                      {project.has_subtitles && (
                        <Badge className="ml-2 bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Toegevoegd
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4">
                      <p className="text-cyan-300 text-sm">
                        <strong>Karaoke-stijl ondertitels:</strong> Woorden worden één voor één gemarkeerd terwijl ze worden uitgesproken. 
                        Perfect voor TikTok en Instagram Reels!
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-300">Tekst voor ondertiteling</Label>
                      <Textarea
                        data-testid="subtitle-text-input"
                        value={voiceText}
                        onChange={(e) => setVoiceText(e.target.value)}
                        rows={3}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                        placeholder="Dezelfde tekst als de voice-over wordt gebruikt voor de ondertitels"
                      />
                      <p className="text-xs text-slate-500">
                        Tip: Gebruik korte, krachtige zinnen voor betere leesbaarheid
                      </p>
                    </div>

                    <Button
                      data-testid="add-subtitles-btn"
                      onClick={handleAddSubtitles}
                      disabled={addingSubtitles || !voiceText.trim() || project.status === "adding_subtitles"}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      {addingSubtitles || project.status === "adding_subtitles" ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Ondertitels toevoegen...
                        </div>
                      ) : (
                        <>
                          <Type className="w-5 h-5 mr-2" />
                          {project.has_subtitles ? "Ondertitels Opnieuw Toevoegen" : "Ondertitels Toevoegen"}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Ad Text */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white text-lg">Advertentietekst</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-slate-300 leading-relaxed">
                    {project.ad_text || "Geen tekst beschikbaar"}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProjectDetail;
